// src/services/apiService.js

import { getIfConfig } from "../config/ifConfig";
import {
  getActiveWorkspaceId,
  registerWorkspaceRequest,
} from "./workspaceSession";
import {
  AUTH_INVALID_REASONS,
  currentReturnTo,
  emitAuthInvalid,
  isAuthInvalidStatus,
  isCookieCsrfFailure,
} from "./authInvalidEvent";

const DEFAULT_UPGRADE_URL = "/pricing";

export class ApiHttpError extends Error {
  constructor(message, payload = {}, status = 0) {
    super(message || "Request failed. Please try again.");
    this.name = "ApiHttpError";
    this.status = status;
    this.payload = payload || {};
    this.code = readErrorCode(payload);
  }
}

export class ApiLimitError extends Error {
  constructor(message, payload = {}, status = 402) {
    super(message || "You’ve reached your current plan limit.");
    this.name = "ApiLimitError";
    this.status = status;
    this.payload = payload || {};

    const error = readErrorObject(payload);
    const metadata =
      error?.metadata && typeof error.metadata === "object"
        ? error.metadata
        : payload?.metadata && typeof payload.metadata === "object"
          ? payload.metadata
          : {};

    this.type = "limit";
    this.kind =
      status === 403 && !isHardLimitPayload(payload, status)
        ? "feature_disabled"
        : "limit_exceeded";

    this.code = String(
      error?.code ||
        payload?.code ||
        metadata?.code ||
        "LIMIT_EXCEEDED"
    ).toUpperCase();

    this.reason = String(
      error?.reason ||
        payload?.reason ||
        metadata?.reason ||
        this.code
    );

    this.meter = String(
      error?.meter ||
        error?.meterName ||
        error?.meter_name ||
        payload?.meter ||
        payload?.meterName ||
        payload?.meter_name ||
        metadata?.meter ||
        metadata?.meterName ||
        metadata?.meter_name ||
        ""
    );

    this.limit = error?.limit ?? payload?.limit ?? metadata?.limit ?? undefined;

    this.used = error?.used ?? payload?.used ?? metadata?.used ?? undefined;

    this.remaining =
      error?.remaining ?? payload?.remaining ?? metadata?.remaining ?? undefined;

    this.resetAt =
      error?.resetAt ||
      error?.reset_at ||
      payload?.resetAt ||
      payload?.reset_at ||
      metadata?.resetAt ||
      metadata?.reset_at ||
      null;

    this.upgradeRequired = Boolean(
      error?.upgradeRequired ??
        error?.upgrade_required ??
        payload?.upgradeRequired ??
        payload?.upgrade_required ??
        true
    );

    this.upgradeUrl = String(
      error?.upgradeUrl ||
        error?.upgrade_url ||
        payload?.upgradeUrl ||
        payload?.upgrade_url ||
        payload?.upgrade_cta?.url ||
        payload?.upgradeCta?.url ||
        DEFAULT_UPGRADE_URL
    );
  }
}

export const isApiLimitError = (error) =>
  error?.name === "ApiLimitError" || error instanceof ApiLimitError;

const LIMIT_ERROR_CODES = new Set([
  "LIMIT_EXCEEDED",
  "PLAN_LIMIT_EXCEEDED",
  "USAGE_LIMIT_EXCEEDED",
  "METER_LIMIT_EXCEEDED",
  "QUOTA_EXCEEDED",
  "SUBSCRIPTION_LIMIT_REACHED",
  "UPGRADE_REQUIRED",
  "AI_ADVISOR_LIMIT_REACHED",
  "SEARCH_LIMIT_REACHED",
  "ALLOWANCE_EXCEEDED",
  "ENTITLEMENT_LIMIT_REACHED",
]);

const FEATURE_DISABLED_CODES = new Set([
  "FEATURE_DISABLED",
  "FEATURE_NOT_INCLUDED",
  "ENTITLEMENT_REQUIRED",
  "PLAN_REQUIRED",
  "UPGRADE_REQUIRED",
]);

const escapeRegExp = (value) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function getCookie(name) {
  if (typeof document === "undefined" || !name) return null;

  const pattern = new RegExp(`(?:^|; )${escapeRegExp(name)}=([^;]*)`);
  const match = document.cookie.match(pattern);

  return match ? decodeURIComponent(match[1]) : null;
}

const resolveAccessToken = async () => {
  try {
    const mod = await import("../context/AuthContext");
    const getter = mod?.getGlobalAccessToken;

    if (typeof getter !== "function") return null;

    const token = await getter();
    return token || null;
  } catch {
    return null;
  }
};

const makeRequestId = () => {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }

  return `req_${Math.random().toString(16).slice(2)}_${Date.now()}`;
};

const safePublicErrorMessage = (status) => {
  if (status === 400) return "Invalid request. Please check your inputs.";
  if (status === 401) return "Session expired. Please sign in again.";
  if (status === 402) return "You’ve reached your current plan limit.";
  if (status === 403) return "Access denied.";
  if (status === 404) return "Not found.";
  if (status === 409) return "Conflict. Please refresh and try again.";
  if (status === 419) return "Session expired. Please sign in again.";
  if (status === 429) return "Too many requests. Please slow down and try again.";
  if (status >= 500) return "Server error. Please try again.";

  return "Request failed. Please try again.";
};

const isFormData = (value) =>
  typeof FormData !== "undefined" && value instanceof FormData;

const isBlob = (value) => typeof Blob !== "undefined" && value instanceof Blob;

const isStringBody = (value) => typeof value === "string";

const supportsBody = (method) => !["GET", "HEAD"].includes(method);

const shouldDebugApi = () => {
  try {
    return (
      sessionStorage.getItem("IF_DEBUG_API") === "1" ||
      localStorage.getItem("IF_DEBUG_API") === "1"
    );
  } catch {
    return false;
  }
};

const debugApi = (...args) => {
  if (shouldDebugApi()) {
    // eslint-disable-next-line no-console
    console.log("[apiRequest]", ...args);
  }
};

const joinApiUrl = (apiUrl, endpoint) => {
  const base = String(apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").startsWith("/")
    ? String(endpoint || "")
    : `/${String(endpoint || "")}`;

  return `${base}${path}`;
};

const parseSuccessResponse = async (response) => {
  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  if (contentType.startsWith("text/")) {
    try {
      return await response.text();
    } catch {
      return null;
    }
  }

  return null;
};

const parseErrorResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    const text = await response.text();

    if (!text) return null;

    return {
      message: text.slice(0, 500),
    };
  } catch {
    return null;
  }
};

const hasHeader = (headers, name) => {
  const target = String(name || "").toLowerCase();

  return Object.keys(headers || {}).some(
    (key) => String(key || "").toLowerCase() === target
  );
};

function readErrorObject(parsed) {
  if (!parsed || typeof parsed !== "object") return {};

  if (parsed.error && typeof parsed.error === "object") {
    return parsed.error;
  }

  return parsed;
}

function readErrorCode(parsed) {
  const error = readErrorObject(parsed);

  const raw =
    error?.code || error?.reason || parsed?.code || parsed?.errorCode || "";

  return String(raw || "").trim();
}

function readErrorMessage(parsed, status) {
  if (!parsed || typeof parsed !== "object") {
    return safePublicErrorMessage(status);
  }

  const error = readErrorObject(parsed);

  const raw =
    error?.message ||
    parsed?.message ||
    parsed?.detail ||
    parsed?.error_description ||
    "";

  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }

  if (typeof parsed.error === "string" && parsed.error.trim()) {
    return parsed.error.trim();
  }

  return safePublicErrorMessage(status);
}

function isHardLimitPayload(parsed, status) {
  if (status === 402) return true;

  const error = readErrorObject(parsed);
  const code = String(error?.code || parsed?.code || "").trim().toUpperCase();
  const reason = String(error?.reason || parsed?.reason || "")
    .trim()
    .toUpperCase();

  if (LIMIT_ERROR_CODES.has(code)) return true;
  if (LIMIT_ERROR_CODES.has(reason)) return true;

  const message = readErrorMessage(parsed, status).toLowerCase();

  return /limit|quota|allowance|usage|exceeded|reached|upgrade/.test(message);
}

function isFeatureDisabledPayload(parsed, status) {
  if (status !== 403) return false;

  const error = readErrorObject(parsed);
  const code = String(error?.code || parsed?.code || "").trim().toUpperCase();
  const reason = String(error?.reason || parsed?.reason || "")
    .trim()
    .toUpperCase();

  if (FEATURE_DISABLED_CODES.has(code)) return true;
  if (FEATURE_DISABLED_CODES.has(reason)) return true;

  const message = readErrorMessage(parsed, status).toLowerCase();

  return /feature|not included|entitlement|plan required|upgrade/.test(message);
}

function isLimitPayload(parsed, status) {
  if (status === 402) return true;

  const error = readErrorObject(parsed);

  if (
    error?.upgradeRequired ||
    error?.upgrade_required ||
    parsed?.upgradeRequired ||
    parsed?.upgrade_required
  ) {
    return true;
  }

  if (status === 403) {
    return (
      isHardLimitPayload(parsed, status) ||
      isFeatureDisabledPayload(parsed, status)
    );
  }

  const code = String(error?.code || parsed?.code || "").trim().toUpperCase();
  const reason = String(error?.reason || parsed?.reason || "")
    .trim()
    .toUpperCase();

  return LIMIT_ERROR_CODES.has(code) || LIMIT_ERROR_CODES.has(reason);
}

function looksLikeCsrfPayload(parsed) {
  const error = readErrorObject(parsed);
  const code = String(error?.code || parsed?.code || "").toUpperCase();
  const reason = String(error?.reason || parsed?.reason || "").toUpperCase();
  const message = readErrorMessage(parsed, 403).toLowerCase();

  return (
    code.includes("CSRF") ||
    reason.includes("CSRF") ||
    message.includes("csrf") ||
    message.includes("security check")
  );
}

function isAuthSessionEndpoint(endpoint) {
  const clean = String(endpoint || "")
    .split("?")[0]
    .replace(/\/+$/, "")
    .toLowerCase();

  return clean === "/auth/session" || clean.endsWith("/auth/session");
}

function isUnauthenticatedSessionProbe({
  endpoint,
  status,
  unauthenticatedAsNull,
}) {
  return (
    (status === 401 || status === 419) &&
    Boolean(unauthenticatedAsNull) &&
    isAuthSessionEndpoint(endpoint)
  );
}

const emitReloginRequired = ({
  reason,
  status,
  code,
  source = "apiService",
} = {}) => {
  emitAuthInvalid(reason || AUTH_INVALID_REASONS.SESSION_EXPIRED, {
    status,
    code,
    source,
    returnTo: currentReturnTo(),
  });
};

const buildLimitError = (parsed, status) => {
  const message = readErrorMessage(parsed, status || 402);
  return new ApiLimitError(message, parsed || {}, status || 402);
};

const buildHttpError = (parsed, status) => {
  const message = readErrorMessage(parsed, status);
  return new ApiHttpError(message, parsed || {}, status);
};

export const apiRequest = async (
  endpoint,
  method = "GET",
  body = null,
  opts = {}
) => {
  const {
    requiresAuth = true,
    timeoutMs = 10000,
    extraHeaders = {},
    signal: callerSignal,
    withCredentials,
    workspaceScoped = true,

    /**
     * Use these for /auth/session bootstrap.
     *
     * /auth/session returning 401 is not an application error.
     * It simply means the visitor is anonymous.
     */
    suppressAuthInvalidEvent = false,
    unauthenticatedAsNull = false,
  } = opts;

  const { API_URL, AUTH_MODE, WITH_CREDENTIALS } = getIfConfig();

  const cleanEndpoint = String(endpoint || "").trim();

  if (!cleanEndpoint) {
    throw new Error("API endpoint is required.");
  }

  const url = joinApiUrl(API_URL, cleanEndpoint);
  const upperMethod = String(method || "GET").toUpperCase();
  const isMutating = !["GET", "HEAD", "OPTIONS"].includes(upperMethod);

  const authMode = String(AUTH_MODE || "").toLowerCase();

  const sendCredentials =
    typeof withCredentials === "boolean"
      ? withCredentials
      : authMode === "cookie"
        ? Boolean(WITH_CREDENTIALS)
        : false;

  const headers = {
    Accept: "application/json",
    "X-Request-Id": makeRequestId(),
    ...extraHeaders,
  };

  const requestWorkspaceId =
    requiresAuth && workspaceScoped ? getActiveWorkspaceId() : null;

  if (requestWorkspaceId && !hasHeader(headers, "X-Workspace-Id")) {
    headers["X-Workspace-Id"] = requestWorkspaceId;
  }

  const canSendBody = supportsBody(upperMethod);
  const hasBody = canSendBody && body !== null && body !== undefined;

  const bodyIsFormData = hasBody && isFormData(body);
  const bodyIsBlob = hasBody && isBlob(body);
  const bodyIsString = hasBody && isStringBody(body);
  const bodyIsJson = hasBody && !bodyIsFormData && !bodyIsBlob && !bodyIsString;

  if (bodyIsJson && !hasHeader(headers, "Content-Type")) {
    headers["Content-Type"] = "application/json";
  }

  /**
   * Only enforce CSRF for authenticated cookie-mode mutating requests.
   * Public unauthenticated calls should not be bounced into relogin just
   * because there is no CSRF cookie yet.
   */
  if (requiresAuth && isMutating && sendCredentials && authMode === "cookie") {
    const csrf = getCookie("if_csrf");

    if (!csrf) {
      if (!suppressAuthInvalidEvent) {
        emitReloginRequired({
          reason: AUTH_INVALID_REASONS.CSRF_MISSING,
          source: "apiService:missing-csrf",
        });
      }

      throw new ApiHttpError("Session expired. Please sign in again.", {}, 419);
    }

    if (!hasHeader(headers, "X-CSRF-Token")) {
      headers["X-CSRF-Token"] = csrf;
    }
  }

  if (requiresAuth && authMode === "amplify") {
    const accessToken = await resolveAccessToken();

    if (accessToken && !hasHeader(headers, "Authorization")) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  const controller = new AbortController();
  const unregisterWorkspaceRequest = requestWorkspaceId
    ? registerWorkspaceRequest(controller)
    : () => {};
  let timedOut = false;

  const onCallerAbort = () => controller.abort();

  if (callerSignal?.addEventListener) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  let requestBody;

  if (hasBody) {
    if (bodyIsFormData || bodyIsBlob || bodyIsString) {
      requestBody = body;
    } else {
      requestBody = JSON.stringify(body);
    }
  }

  debugApi("request:start", {
    method: upperMethod,
    url,
    hasBody,
    sendCredentials,
    authMode,
    requiresAuth,
    suppressAuthInvalidEvent,
    unauthenticatedAsNull,
    headers: Object.keys(headers),
    bodyPreview:
      bodyIsJson && body && typeof body === "object"
        ? Object.keys(body)
        : bodyIsString
          ? "string"
          : bodyIsFormData
            ? "formdata"
            : bodyIsBlob
              ? "blob"
              : null,
  });

  try {
    const response = await fetch(url, {
      method: upperMethod,
      headers,
      body: hasBody ? requestBody : undefined,
      signal: controller.signal,
      credentials: sendCredentials ? "include" : "omit",
      cache: upperMethod === "GET" ? "no-store" : "default",
    });

    debugApi("request:response", {
      method: upperMethod,
      url,
      status: response.status,
      ok: response.ok,
    });

    const parsedError = !response.ok ? await parseErrorResponse(response) : null;

    /**
     * Critical auth bootstrap fix:
     *
     * GET /auth/session -> 401 means "anonymous visitor".
     * It must not emit AUTH_INVALID_EVENT.
     * It must not force logout.
     * It must not keep the login page stuck on "Checking your session…".
     */
    if (
      isUnauthenticatedSessionProbe({
        endpoint: cleanEndpoint,
        status: response.status,
        unauthenticatedAsNull,
      })
    ) {
      debugApi("request:unauthenticated-session-probe", {
        method: upperMethod,
        url,
        status: response.status,
      });

      return null;
    }

    /**
     * Order matters:
     * 1. Business plan/limit responses first.
     * 2. Real auth expiry next.
     * 3. CSRF only when the 403 payload actually looks like CSRF.
     */
    if (response.status === 402) {
      throw buildLimitError(parsedError, response.status);
    }

    if (response.status === 403 && isLimitPayload(parsedError, response.status)) {
      throw buildLimitError(parsedError, response.status);
    }

    const authInvalidButNotBusiness403 = isAuthInvalidStatus(response.status);

    if (authInvalidButNotBusiness403) {
      const code = readErrorCode(parsedError);

      if (requiresAuth && !suppressAuthInvalidEvent) {
        emitReloginRequired({
          reason:
            response.status === 419
              ? AUTH_INVALID_REASONS.CSRF_FAILED
              : AUTH_INVALID_REASONS.SESSION_EXPIRED,
          status: response.status,
          code,
          source: "apiService:auth-invalid-status",
        });
      }

      throw new ApiHttpError(
        "Session expired. Please sign in again.",
        parsedError || {},
        response.status
      );
    }

    if (
      requiresAuth &&
      response.status === 403 &&
      isCookieCsrfFailure({
        status: response.status,
        isMutating,
        sendCredentials,
        authMode,
      }) &&
      looksLikeCsrfPayload(parsedError)
    ) {
      const code = readErrorCode(parsedError);

      if (!suppressAuthInvalidEvent) {
        emitReloginRequired({
          reason: AUTH_INVALID_REASONS.CSRF_FAILED,
          status: response.status,
          code,
          source: "apiService:csrf-403",
        });
      }

      throw new ApiHttpError(
        "Session expired. Please sign in again.",
        parsedError || {},
        response.status
      );
    }

    if (!response.ok) {
      throw buildHttpError(parsedError, response.status);
    }

    return await parseSuccessResponse(response);
  } catch (error) {
    debugApi("request:error", {
      method: upperMethod,
      url,
      name: error?.name,
      message: error?.message,
      status: error?.status,
      code: error?.code,
      reason: error?.reason,
      meter: error?.meter,
      timedOut,
    });

    if (error?.name === "AbortError") {
      if (timedOut) {
        throw new ApiHttpError("Request timed out. Please try again.", {}, 408);
      }

      throw new ApiHttpError("Request was cancelled.", {}, 0);
    }

    throw error;
  } finally {
    unregisterWorkspaceRequest();
    clearTimeout(timeoutId);

    if (callerSignal?.removeEventListener) {
      callerSignal.removeEventListener("abort", onCallerAbort);
    }
  }
};